/**
 * =============================================================================
 * Página /tenant/content/templates/:templateId — Construtor de modelos (Studio)
 *
 * O documento renderizado é a própria superfície de edição. Biblioteca e
 * propriedades compartilham um painel contextual à esquerda, aberto somente
 * quando o usuário adiciona ou seleciona um bloco.
 *
 * Regras respeitadas:
 *  - toda leitura/escrita passa por RPCs (`src/lib/studio/api.ts`);
 *  - publicar valida CAPACIDADES do tipo de modelo antes de chamar o servidor;
 *  - o Modo Código (HTML) é restrito a administradores da conta.
 *
 * @version 2.4.0
 * RELEASE: 2026-09-11 v2.4.0 - Mantém a biblioteca ampla, mas reduz o painel
 *          de edição no desktop para a largura compatível com seus campos.
 * RELEASE: 2026-09-11 v2.3.0 - HTML importado passa a virar seções editáveis
 *          (blocos `section.html`) em vez de substituir o modelo por código.
 * RELEASE: 2026-09-10 v2.2.0 - Painel "Adicionar bloco" mais largo e sem borda
 *          externa, aproveitando melhor o espaço dos cartões.
 * RELEASE: 2026-09-10 v2.1.0 - Painel contextual claro e sobreposto ao canvas,
 *          com largura ampliada no desktop e Sheet no mobile.
 * RELEASE: 2026-09-10 v2.0.0 - Editor em tela única, expandido por padrão,
 *          canvas visual e painéis contextuais somente à esquerda.
 * RELEASE: 2026-09-10 v1.3.0 - Construtor por blocos prontos: biblioteca em dois
 *          níveis, pontos "Adicionar bloco" no documento e modo tela cheia.
 * RELEASE: 2026-09-10 v1.2.0 - Aba "Envios" das páginas públicas (visitas,
 *          busca, paginação e origem do visitante).
 * RELEASE: 2026-09-10 v1.1.0 - Importar HTML com conferência no servidor e
 *          histórico de versões (conclusão da Fase 4).
 * RELEASE: 2026-09-09 v1.0.0 - Versão inicial (Studio, Fase 4).
 * =============================================================================
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Code2,
  FileJson,
  FileUp,
  History,
  Inbox,
  ExternalLink,
  Globe,
  Loader2,
  Maximize2,
  Minimize2,
  Monitor,
  Redo2,
  Save,
  Send,
  Smartphone,
  Undo2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { InlineNotice } from '@/components/ui/inline-notice';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';

import { StudioPatternLibrary } from '@/components/studio/StudioPatternLibrary';
import { StudioCanvas } from '@/components/studio/StudioCanvas';
import { StudioPropertiesPanel } from '@/components/studio/StudioPropertiesPanel';
import { StudioHtmlImportDialog } from '@/components/studio/StudioHtmlImportDialog';
import { StudioVersionHistory } from '@/components/studio/StudioVersionHistory';
import { StudioPagePublishDialog } from '@/components/studio/StudioPagePublishDialog';
import { StudioJsonPanel } from '@/components/studio/StudioJsonPanel';
import { StudioPageSubmissions } from '@/components/studio/StudioPageSubmissions';


import { buildPublicPageUrl, previewPage } from '@/lib/studio/api';
import { useStudioEditor } from '@/lib/studio/editor/useStudioEditor';
import {
  createEmptyDocument,
  getTemplateDefinition,
  validateForPublish,
  type StudioDocument,
  type StudioOutput,
} from '@/lib/studio/core';
import {
  usePublishStudioPage,
  usePublishStudioTemplate,
  useSaveStudioDraft,
  useStudioTemplate,
} from '@/hooks/useStudioTemplates';
import { useAuth } from '@/hooks/useAuth';
import { useStudioStockCatalog } from '@/hooks/useStudioStockCatalog';
import { useIsMobile } from '@/hooks/use-mobile';
import type { StudioHtmlSectionData } from '@/lib/studio/html/section';
import { toast } from '@/lib/toast';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils';

/**
 * Áreas do editor; a prévia visual agora é a própria área de montagem.
 * `json` e `html` substituem a antiga aba única "Código", deixando explícito
 * onde colar cada formato (padrão adotado por editores de referência).
 */
type AbaCentral = 'estrutura' | 'json' | 'html' | 'envios';

/** Painel auxiliar aberto sobre o lado esquerdo do canvas. */
type PainelContextual = 'fechado' | 'adicionar' | 'editar';

export default function StudioTemplateEditor() {
  // Acervo compartilhado de imagens: alimenta o resolvedor de `acervo://`
  useStudioStockCatalog();

  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const { isTenantAdmin, isRootAdmin } = useAuth();
  const isMobile = useIsMobile();
  const podeUsarCodigo = isTenantAdmin || isRootAdmin;

  const { data, isLoading } = useStudioTemplate(templateId ?? null);
  const salvar = useSaveStudioDraft();
  const publicar = usePublishStudioTemplate();
  const publicarPagina = usePublishStudioPage();

  const editor = useStudioEditor(null);
  const { replaceDocument, markSaved } = editor;

  const [aba, setAba] = useState<AbaCentral>('estrutura');
  const [dispositivo, setDispositivo] = useState<'desktop' | 'mobile'>('desktop');
  const [htmlBruto, setHtmlBruto] = useState('');
  const [carregado, setCarregado] = useState(false);
  const [importando, setImportando] = useState(false);
  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [publicarPaginaAberto, setPublicarPaginaAberto] = useState(false);
  // Indica que a prévia temporária da página está sendo gerada
  const [gerandoPrevia, setGerandoPrevia] = useState(false);
  // Biblioteca e propriedades ocupam alternadamente o mesmo painel contextual.
  const [painelContextual, setPainelContextual] = useState<PainelContextual>('fechado');
  const [indiceInsercao, setIndiceInsercao] = useState<number | null>(null);
  // O Studio abre expandido por padrão, como o editor de fluxos.
  const [telaCheia, setTelaCheia] = useState(true);


  const rascunho = data?.draft_version ?? null;
  const modelo = data?.template ?? null;
  const saidas: StudioOutput[] = (modelo?.default_output_modes as StudioOutput[]) ?? ['web'];
  /**
   * Modo Código: começa pelo formato gravado no rascunho, mas pode mudar na
   * sessão (importar HTML liga; importar JSON de blocos desliga).
   */
  const [modoCodigo, setModoCodigo] = useState(false);

  // Carrega o rascunho no editor uma única vez por versão aberta
  useEffect(() => {
    if (!rascunho || carregado) return;
    const conteudo = (rascunho.source_content ?? createEmptyDocument()) as StudioDocument;
    replaceDocument(conteudo, false);
    setHtmlBruto(String((conteudo.settings as Record<string, unknown>)?.html ?? ''));
    const ehHtml = rascunho.source_format === 'raw_html';
    setModoCodigo(ehHtml);
    if (ehHtml) setAba('html');
    setCarregado(true);
  }, [rascunho, carregado, replaceDocument]);

  /** Documento efetivo considerando o Modo Código. */
  const documentoAtual: StudioDocument = useMemo(() => {
    if (!modoCodigo) return editor.document;
    return {
      ...editor.document,
      blocks: [],
      settings: { ...(editor.document.settings ?? {}), html: htmlBruto },
    };
  }, [editor.document, htmlBruto, modoCodigo]);

  /** Problemas que impedem a publicação (validação por capacidade). */
  const problemas = useMemo(() => {
    if (!modelo || modoCodigo) return [];
    return validateForPublish(editor.document, modelo.type, saidas).issues;
  }, [editor.document, modelo, modoCodigo, saidas]);

  async function aoSalvar() {
    if (!modelo || !templateId) return;
    try {
      await salvar.mutateAsync({
        templateId,
        name: modelo.name,
        type: modelo.type,
        slug: modelo.slug,
        description: modelo.description,
        sourceFormat: modoCodigo ? 'raw_html' : 'blocks',
        document: documentoAtual,
        outputModes: saidas,
      });
      markSaved();
      toast.success('Rascunho salvo.');
    } catch (erro) {
      logger.error('studio', 'Falha ao salvar rascunho', erro);
      toast.error('Não foi possível salvar o rascunho.');
    }
  }

  /** Grava o que está na tela antes de qualquer publicação. */
  /**
   * Abre a prévia da página em uma nova aba.
   * O rascunho é salvo antes, para a prévia mostrar exatamente o que está na tela.
   */
  async function aoAbrirPreviaPublica() {
    if (!templateId) return;
    // A aba é aberta antes da chamada para não ser bloqueada pelo navegador.
    const aba = window.open('', '_blank', 'noopener');
    setGerandoPrevia(true);
    try {
      await gravarAntesDePublicar();
      markSaved();
      const { preview_url } = await previewPage(templateId);
      if (aba) aba.location.href = preview_url;
      else window.open(preview_url, '_blank', 'noopener');
    } catch (erro) {
      aba?.close();
      logger.error('studio', 'Falha ao abrir prévia da página', erro);
      toast.error('Não foi possível abrir a prévia da página.');
    } finally {
      setGerandoPrevia(false);
    }
  }

  async function gravarAntesDePublicar() {
    if (!modelo || !templateId) return;
    await salvar.mutateAsync({
      templateId,
      name: modelo.name,
      type: modelo.type,
      slug: modelo.slug,
      description: modelo.description,
      sourceFormat: modoCodigo ? 'raw_html' : 'blocks',
      document: documentoAtual,
      outputModes: saidas,
    });
  }

  async function aoPublicar() {
    if (!modelo || !templateId) return;
    if (problemas.length) {
      toast.error('Ajuste os pontos indicados antes de publicar.');
      return;
    }
    // Páginas públicas precisam de endereço e dados de busca antes de ir ao ar
    if (modelo.type === 'landing_page') {
      setPublicarPaginaAberto(true);
      return;
    }
    try {
      await gravarAntesDePublicar();
      await publicar.mutateAsync({ templateId });
      markSaved();
      toast.success('Modelo publicado.');
    } catch (erro) {
      logger.error('studio', 'Falha ao publicar modelo', erro);
      toast.error('Não foi possível publicar o modelo.');
    }
  }

  /** Publica a página pública com endereço e dados de busca informados. */
  async function aoPublicarPagina(dados: {
    path: string;
    seo: {
      title: string;
      description: string;
      image_url: string;
      canonical_url: string;
      indexable: boolean;
    };
  }) {
    if (!templateId) return;
    try {
      await gravarAntesDePublicar();
      await publicarPagina.mutateAsync({ templateId, path: dados.path, seo: dados.seo });
      markSaved();
      setPublicarPaginaAberto(false);
      toast.success('Página publicada.');
    } catch (erro) {
      logger.error('studio', 'Falha ao publicar página pública', erro);
      toast.error(
        erro instanceof Error ? erro.message : 'Não foi possível publicar a página.',
      );
    }
  }

  /** Coloca no ar uma versão anterior a partir do histórico. */
  async function aoPublicarVersao(versionId: string) {
    if (!templateId) return;
    try {
      await publicar.mutateAsync({ templateId, versionId });
      toast.success('Versão colocada no ar.');
      setHistoricoAberto(false);
    } catch (erro) {
      logger.error('studio', 'Falha ao publicar versão do histórico', erro);
      toast.error('Não foi possível colocar essa versão no ar.');
    }
  }

  /**
   * Recebe o conteúdo já limpo pelo servidor.
   *
   * Caminho normal: a página entra FATIADA em blocos `section.html`, que o
   * usuário move, duplica, apaga e edita como qualquer outro bloco. Só quando
   * não é possível fatiar (conteúdo sem estrutura) caímos no Modo Código.
   */
  function aoImportarHtml(secoes: StudioHtmlSectionData[], htmlLimpo: string) {
    if (secoes.length) {
      const blocos = secoes.map((secao, indice) => ({
        id: `html-${Date.now().toString(36)}-${indice}`,
        type: 'section.html',
        props: {
          title: secao.title,
          bleed: 'full',
          html: secao.html,
          css: secao.css,
          fields: secao.fields,
          content: secao.content,
        },
      }));
      setModoCodigo(false);
      setHtmlBruto('');
      const settings = { ...(editor.document.settings ?? {}) } as Record<string, unknown>;
      delete settings.html;
      replaceDocument({ ...editor.document, blocks: blocos, settings }, true);
      setAba('estrutura');
      toast.success(`Página importada em ${blocos.length} seção(ões) editáveis.`);
      return;
    }

    setHtmlBruto(htmlLimpo);
    setModoCodigo(true);
    replaceDocument(
      {
        ...editor.document,
        blocks: [],
        settings: { ...(editor.document.settings ?? {}), html: htmlLimpo },
      },
      true,
    );
    setAba('html');
    toast.success('Conteúdo importado. Revise e salve o rascunho.');
  }

  /**
   * Recebe o documento de blocos já normalizado/conferido e volta ao modo
   * estruturado, para o usuário continuar editando visualmente.
   */
  function aoImportarJson(documento: StudioDocument) {
    setModoCodigo(false);
    setHtmlBruto('');
    const settings = { ...(documento.settings ?? {}) } as Record<string, unknown>;
    delete settings.html;
    replaceDocument({ ...documento, settings }, true);
    setAba('estrutura');
  }

  /** Abre a edição contextual do bloco selecionado. */
  function aoSelecionarBloco(id: string) {
    editor.select(id);
    setIndiceInsercao(null);
    setPainelContextual('editar');
  }

  /** Abre a biblioteca já vinculada ao ponto escolhido no documento. */
  function aoSolicitarInsercao(indice: number) {
    setIndiceInsercao(indice);
    setPainelContextual('adicionar');
  }

  /** Fecha qualquer painel sem alterar o conteúdo nem a seleção atual. */
  function aoFecharPainel() {
    setPainelContextual('fechado');
    setIndiceInsercao(null);
  }

  if (isLoading || !modelo) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const definicaoTipo = getTemplateDefinition(modelo.type);
  const ocupado = salvar.isPending || publicar.isPending || publicarPagina.isPending;

  /** Conteúdo único reutilizado no cartão desktop e no Sheet mobile. */
  const conteudoPainel = painelContextual === 'adicionar' ? (
    <StudioPatternLibrary
      outputs={saidas}
      templateType={modelo.type}
      insertIndex={indiceInsercao}
      onInsertPattern={(chave, indice) => {
        editor.insertPattern(chave, indice ?? undefined);
        aoFecharPainel();
      }}
      onInsertBlock={(chave, indice) => {
        if (indice === null) editor.addBlock(chave);
        else editor.insertAt(indice, [chave]);
        aoFecharPainel();
      }}
      onClose={aoFecharPainel}
    />
  ) : (
    <StudioPropertiesPanel
      node={editor.selectedNode}
      onChange={(chave, valor) =>
        editor.selectedId && editor.updateProp(editor.selectedId, chave, valor)
      }
      onClose={aoFecharPainel}
    />
  );

  return (
    <div
      className={cn(
        'flex flex-col font-body',
        telaCheia ? 'fixed inset-0 z-50 h-screen bg-studio-panel text-foreground' : 'h-full bg-background',
      )}
    >

      {/* ---------------------------------------------------------- Cabeçalho */}
      <div className="flex min-h-14 flex-wrap items-center gap-2 border-b bg-studio-panel px-3 py-2 text-foreground shadow-sm">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => navigate('/tenant/content/templates')}
          aria-label="Voltar para a lista"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>

        <div className="min-w-0">
          <p className="truncate font-display text-sm font-semibold">{modelo.name}</p>
          <p className="text-[11px] text-muted-foreground">
            {definicaoTipo?.label ?? modelo.type} · {saidas.join(', ')}
          </p>
        </div>

        {editor.isDirty && (
          <Badge variant="secondary" className="h-5 text-[10px]">
            Alterações não salvas
          </Badge>
        )}

        <div className="ml-auto flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={!editor.canUndo}
            onClick={editor.undo}
            aria-label="Desfazer"
          >
            <Undo2 className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={!editor.canRedo}
            onClick={editor.redo}
            aria-label="Refazer"
          >
            <Redo2 className="h-4 w-4" />
          </Button>

          <Separator orientation="vertical" className="mx-1 h-5" />

          <Button
            type="button"
            variant={dispositivo === 'desktop' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-8 w-8"
            onClick={() => setDispositivo('desktop')}
            aria-label="Ver como computador"
          >
            <Monitor className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant={dispositivo === 'mobile' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-8 w-8"
            onClick={() => setDispositivo('mobile')}
            aria-label="Ver como celular"
          >
            <Smartphone className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            variant={telaCheia ? 'secondary' : 'ghost'}
            size="icon"
            className="h-8 w-8"
            onClick={() => setTelaCheia((atual) => !atual)}
            aria-label={telaCheia ? 'Sair da tela cheia' : 'Editar em tela cheia'}
          >
            {telaCheia ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>

          <Separator orientation="vertical" className="mx-1 h-5" />

          {podeUsarCodigo && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5"
              onClick={() => setImportando(true)}
            >
              <FileUp className="h-3.5 w-3.5" />
              Importar HTML
            </Button>
          )}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5"
            onClick={() => setHistoricoAberto(true)}
          >
            <History className="h-3.5 w-3.5" />
            Versões
          </Button>

          {/* Páginas públicas: link temporário de conferência e link no ar */}
          {modelo.type === 'landing_page' && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5"
                disabled={gerandoPrevia}
                onClick={aoAbrirPreviaPublica}
              >
                {gerandoPrevia ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <ExternalLink className="h-3.5 w-3.5" />
                )}
                Ver prévia
              </Button>

              {data?.publication?.status === 'published' && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-1.5"
                  onClick={() =>
                    window.open(buildPublicPageUrl(data.publication!.path), '_blank', 'noopener')
                  }
                >
                  <Globe className="h-3.5 w-3.5" />
                  Abrir página
                </Button>
              )}
            </>
          )}

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1.5"
            onClick={aoSalvar}
            disabled={ocupado}
          >
            {salvar.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Salvar
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-8 gap-1.5"
            onClick={aoPublicar}
            disabled={ocupado || problemas.length > 0}
          >
            {publicar.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
            Publicar
          </Button>
        </div>
      </div>

      {/* Áreas auxiliares; montar já é a prévia oficial em tempo real. */}
      <div className="flex items-center gap-1 border-b bg-studio-panel px-3 py-1.5 text-foreground">
        {([
          { key: 'estrutura' as const, label: 'Design', icon: Monitor },
          // JSON: documento de blocos (pode ser gerado por IA a partir do catálogo)
          { key: 'json' as const, label: 'JSON', icon: FileJson },
          // HTML: página pronta importada; exige permissão de código
          ...(podeUsarCodigo ? [{ key: 'html' as const, label: 'HTML', icon: Code2 }] : []),
          // Acompanhamento dos envios só faz sentido em páginas públicas
          ...(modelo.type === 'landing_page'
            ? [{ key: 'envios' as const, label: 'Envios', icon: Inbox }]
            : []),
        ]).map((item) => (
          <Button
            key={item.key}
            type="button"
            variant={aba === item.key ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 gap-1.5 text-xs"
            onClick={() => setAba(item.key)}
          >
            <item.icon className="h-3.5 w-3.5" />
            {item.label}
          </Button>
        ))}
      </div>

      {problemas.length > 0 && (
        <div className="px-3 pt-2">
          <InlineNotice variant="warning">
            <p className="font-medium">Falta algo para publicar</p>
            <ul className="list-disc pl-4">
              {problemas.slice(0, 4).map((p, i) => (
                <li key={`${p.code}-${i}`}>{p.message}</li>
              ))}
            </ul>
          </InlineNotice>
        </div>
      )}

      {/* ---------------------------------------------------------- Conteúdo */}
      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        {aba === 'estrutura' && (
          <>
            <div className="min-w-0 flex-1">
              <StudioCanvas
                document={editor.document}
                selectedId={editor.selectedId}
                device={dispositivo}
                onSelect={aoSelecionarBloco}
                onMove={editor.moveBlock}
                onDuplicate={editor.duplicateBlock}
                onRemove={editor.removeBlock}
                insertIndex={indiceInsercao}
                onRequestInsert={aoSolicitarInsercao}
              />
            </div>

            {/* O painel flutua sobre o documento e nunca reduz a largura do canvas. */}
            {!isMobile && painelContextual !== 'fechado' && (
              <div className="pointer-events-none absolute inset-0 z-40">
                <button
                  type="button"
                  aria-label="Fechar painel contextual"
                  onClick={aoFecharPainel}
                  className="pointer-events-auto absolute inset-0 cursor-default bg-foreground/15"
                />
                <aside
                  className={cn(
                    'pointer-events-auto absolute inset-y-3 left-3 flex overflow-hidden rounded-xl bg-card shadow-xl',
                    painelContextual === 'adicionar'
                      ? 'w-[min(840px,calc(100%-24px))]'
                      : 'w-[min(400px,calc(100%-24px))]',
                  )}
                >
                  {conteudoPainel}
                </aside>
              </div>
            )}

            {/* Em telas pequenas, segue o mesmo padrão de Sheet do Workflow. */}
            {isMobile && (
              <Sheet
                open={painelContextual !== 'fechado'}
                onOpenChange={(aberto) => !aberto && aoFecharPainel()}
              >
                <SheetContent side="left" className="w-[96vw] max-w-none overflow-hidden p-0 sm:max-w-none">
                  <SheetTitle className="sr-only">
                    {painelContextual === 'adicionar' ? 'Adicionar bloco' : 'Editar bloco'}
                  </SheetTitle>
                  {conteudoPainel}
                </SheetContent>
              </Sheet>
            )}
          </>
        )}


        {aba === 'envios' && templateId && (
          <StudioPageSubmissions templateId={templateId} />
        )}

        {aba === 'json' && (
          <div className="min-h-0 flex-1 overflow-auto p-3">
            <StudioJsonPanel
              documentoAtual={editor.document}
              tipoDocumento={modelo.type}
              templateId={templateId ?? null}
              podeImportar
              onAplicar={aoImportarJson}
            />
          </div>
        )}

        {aba === 'html' && podeUsarCodigo && (
          <div className="flex min-h-0 flex-1 flex-col gap-2 p-3">
            <InlineNotice variant="info">
              O conteúdo é limpo pelo servidor ao salvar. Nenhum script é executado. Para encaixar
              comportamentos do sistema, use marcadores como
              {' '}<code>&lt;div data-uaizy-block="action.proposal.decision"&gt;&lt;/div&gt;</code>.
            </InlineNotice>
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5"
                onClick={() => setImportando(true)}
              >
                <FileUp className="h-3.5 w-3.5" />
                Importar HTML
              </Button>
            </div>
            <Textarea
              value={htmlBruto}
              onChange={(e) => setHtmlBruto(e.target.value)}
              placeholder="Cole aqui o HTML do documento"
              className="min-h-0 flex-1 font-mono text-xs"
            />
          </div>
        )}
      </div>

      {/* ------------------------------------------------ Janelas auxiliares */}
      <StudioHtmlImportDialog
        open={importando}
        onOpenChange={setImportando}
        templateId={templateId ?? null}
        onConfirm={aoImportarHtml}
      />

      <StudioVersionHistory
        open={historicoAberto}
        onOpenChange={setHistoricoAberto}
        versions={data?.versions ?? []}
        publishedVersionId={modelo.current_published_version_id}
        publishing={publicar.isPending}
        onPublish={aoPublicarVersao}
      />

      {/* Endereço e dados de busca da página pública */}
      <StudioPagePublishDialog
        open={publicarPaginaAberto}
        onOpenChange={setPublicarPaginaAberto}
        publicacao={data?.publication ?? null}
        nomeModelo={modelo.name}
        publicando={publicarPagina.isPending || salvar.isPending}
        onConfirm={aoPublicarPagina}
      />
    </div>
  );
}
